import React, { forwardRef, useCallback } from 'react';
import { Link as RouterLink, NavLink as RouterNavLink, useNavigate as useRouterNavigate, useLocation as useRouterLocation, type LinkProps, type NavLinkProps, type NavigateOptions, type To } from 'react-router-dom';
import { useLocale } from './LocaleProvider';
import { englishPath, type Locale } from './locale';
import { localizeHref } from './availability';
import { localizedAsset } from './Localize';

function localizedTo(to: To, locale: Locale): To {
  return typeof to === 'string' ? localizeHref(to, locale) : { ...to, ...(to.pathname ? { pathname: localizeHref(to.pathname, locale) } : {}) };
}
export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const { locale } = useLocale();
  return <RouterLink {...props} to={props.lang === 'en' ? props.to : localizedTo(props.to, locale)} ref={ref} />;
});
Link.displayName = 'LocalizedLink';
export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>((props, ref) => {
  const { locale } = useLocale();
  return <RouterNavLink {...props} to={props.lang === 'en' ? props.to : localizedTo(props.to, locale)} ref={ref} />;
});
NavLink.displayName = 'LocalizedNavLink';
export const LocaleAnchor = forwardRef<HTMLAnchorElement, React.AnchorHTMLAttributes<HTMLAnchorElement>>((props, ref) => {
  const { locale } = useLocale();
  // lang or hrefLang "en" links the English site (hrefLang when the link text itself is Portuguese).
  const target = props.lang === 'en' || props.hrefLang === 'en' ? 'en' : locale;
  return <a {...props} href={props.href ? localizeHref(props.href, target) : undefined} ref={ref} />;
});
LocaleAnchor.displayName = 'LocaleAnchor';
export const LocaleImage = forwardRef<HTMLImageElement, React.ImgHTMLAttributes<HTMLImageElement>>((props, ref) => {
  const { locale } = useLocale();
  return <img {...props} src={props.src ? localizedAsset(props.src, locale) : undefined} ref={ref} />;
});
LocaleImage.displayName = 'LocaleImage';
export function useNavigate() {
  const navigate = useRouterNavigate();
  const { locale } = useLocale();
  return useCallback((to: To | number, options?: NavigateOptions) => {
    if (typeof to === 'number') navigate(to);
    else navigate(localizedTo(to, locale), options);
  }, [navigate, locale]);
}
/** Existing active-menu/home checks intentionally compare unprefixed route identities. */
export function useLocation() {
  const location = useRouterLocation();
  return { ...location, pathname: englishPath(location.pathname) };
}
